import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { SymbolType } from '../enum/SymbolType'
import { ISpinStore } from '../../spin/store/spinStore'
import { SymbolData, GameResult, MGResult, GodziDropResult, Drop } from '../model'
import { GodziDropResultType } from '@slot-nx/core'
import type { SlotPotRecord } from '@slot-nx/pot'
import { BetOptionType } from '../../placeOrder/utils/stakeUtils'

export enum GameStatus {
  Normal,
  Godzi,
  Voucher,
}

export const defaultTableList: SymbolData[][] = [
  [
    { type: SymbolType.L5, multiplier: 0 },
    { type: SymbolType.L5, multiplier: 0 },
    { type: SymbolType.C1Wild, multiplier: 0 },
    { type: SymbolType.H2, multiplier: 0 },
    { type: SymbolType.H2, multiplier: 0 },
  ],
  [
    { type: SymbolType.L5, multiplier: 0 },
    { type: SymbolType.H1, multiplier: 0 },
    { type: SymbolType.H1, multiplier: 0 },
    { type: SymbolType.L1, multiplier: 0 },
    { type: SymbolType.H1, multiplier: 0 },
  ],
  [
    { type: SymbolType.L3, multiplier: 0 },
    { type: SymbolType.C1Expanding, multiplier: 0 },
    { type: SymbolType.L2, multiplier: 0 },
    { type: SymbolType.L2, multiplier: 0 },
    { type: SymbolType.L2, multiplier: 0 },
  ],
  [
    { type: SymbolType.L3, multiplier: 0 },
    { type: SymbolType.H3, multiplier: 0 },
    { type: SymbolType.S1, multiplier: 500 },
    { type: SymbolType.C1Multiplier, multiplier: 0 },
    { type: SymbolType.C1Expanding, multiplier: 0 },
  ],
  [
    { type: SymbolType.L3, multiplier: 0 },
    { type: SymbolType.L1, multiplier: 0 },
    { type: SymbolType.L1, multiplier: 0 },
    { type: SymbolType.L1, multiplier: 0 },
    { type: SymbolType.L4, multiplier: 0 },
  ],
  [
    { type: SymbolType.L1, multiplier: 0 },
    { type: SymbolType.C1Expanding, multiplier: 0 },
    { type: SymbolType.C1Multiplier, multiplier: 0 },
    { type: SymbolType.H4, multiplier: 0 },
    { type: SymbolType.H3, multiplier: 0 },
  ],
]

export const resetInitData = () => {
  useGameStore.setState({
    tableList: defaultTableList,
    gameResult: initGameResult,
    betOptionType: BetOptionType.MainGame,
    isReadyToStop: false,
  })
}

const initDrop: Drop = {
  dropTable: defaultTableList,
  finalTable: defaultTableList,
  payout: 0,
  totalWinPositions: [],
  winLineList: [],
  giftedSymbols: [],
  expandingSymbols: [],
  isExpanding: false,
  isGiveWild: false,
  isGiveMultiplier: false,
}

const initMGResult: MGResult = {
  dropList: [initDrop],
  betLevel: 0,
  odds: 0,
  payout: 0,
  playStake: 0,
  freeGameType: 0,
  fgAddTimes: 0,
  potActionList: [],
  multiplierList: [],
  spinMultiplier: 0,
  totalMultiplier: 0,
  accMultiplier: 0,
  basePayout: 0,
}

const initGameResult: GameResult = {
  fgPayout: 0,
  mgResult: initMGResult,
  fgResultList: [],
  totalPayout: 0,
}

const initGodziResult: GodziDropResult = {
  effectiveStake: 0,
  freeSpinCount: 0,
  gameResultList: [],
  payout: 0,
  prize: 0,
  type: GodziDropResultType.None,
  winLose: 0,
}

export interface IGameStore extends ISpinStore<SymbolData> {
  gameStatus: GameStatus
  gameResult: GameResult
  tableList: SymbolData[][]
  isReadyToStop: boolean
  isFreeGame: boolean
  showMultiplier: boolean
  isShowSpinLoading: boolean
  betOptionType: BetOptionType
  /** 上一次實際下注的金額（betOptionType 下單後就被重設，session 記帳需要原值）；voucher 局為 0 */
  lastPayStake: number
  /** WinBoard 的 win/return 文字要不要顯示「Win」；由 WinningService.updateWinLabel 依 payout vs lastPayStake 算出後寫入，
   *  WinBoard 的 JSX text prop 要讀這個欄位反應式渲染，不能只靠 getChildByName imperative 改字——那個字每次 WinBoard
   *  重新渲染（例如 enableBuyBonus/isFreeGame 變動）都會被 JSX 宣告的預設字重設回去。 */
  isMainGameWin: boolean
  /** Per-pot progress keyed by `POT_RECORD_KEY`; write-through to sessionStorage. */
  pots: Record<string, SlotPotRecord>
  dropIndex: number
  freeGameIndex: number
  freeGameConut: number
  isExpand: boolean
  hasRefillTable: boolean
  combolList: { x: number, y: number, type: number, name: string }[]
  freeGameType: number
  totalMultiplier: number
  totalFreeMultiplier: number
  isVoucher: () => boolean
  getGameResult: () => MGResult
  getDropItem: () => Drop
  getRemainFreeSpin: () => number
}

export const useGameStore = create<IGameStore>()(
  subscribeWithSelector<IGameStore>((set, get) => ({
    gameResult: initGameResult,
    gameStatus: GameStatus.Normal,
    isReadyToStop: false,
    godziDropResult: initGodziResult,
    tableList: defaultTableList,
    isFreeGame: false,
    showMultiplier: false,
    betOptionType: BetOptionType.MainGame,
    lastPayStake: 0,
    isMainGameWin: true,
    pots: {},
    dropIndex: 0,
    freeGameIndex: -1,
    freeGameConut: 0,
    isExpand: false,
    hasRefillTable: false,
    totalMultiplier: 0,
    totalFreeMultiplier: 0,
    combolList: [],
    freeGameType: 0,
    isShowSpinLoading: false,
    isVoucher: () => {
      const store = get()
      return store.gameStatus === GameStatus.Voucher
    },
    getGameResult: () => {
      const store = get()
      const freeGameIndex = store.freeGameIndex
      const result = store.freeGameIndex === -1
        ? store.gameResult.mgResult
        : store.gameResult.fgResultList[freeGameIndex]
      return result
    },
    getDropItem: () => {
      const store = get()
      const dropIndex = store.dropIndex
      const freeGameIndex = store.freeGameIndex
      const dropItem = store.freeGameIndex === -1
        ? store.gameResult.mgResult.dropList[dropIndex]
        : store.gameResult.fgResultList?.[freeGameIndex]?.dropList?.[dropIndex]
      return dropItem as Drop
    },
    getRemainFreeSpin: () => {
      const store = get()
      if (store.freeGameIndex === -1) {
        return 0
      }
      else {
        const freeGameIndex = store.freeGameIndex
        const freeGameList = store.gameResult.fgResultList
        const fgResult = freeGameList?.[freeGameIndex]
        if (!fgResult) return 0
        const totalFreeSpins = freeGameList.length
        return totalFreeSpins - freeGameIndex
      }
    },
  })),
)
